/**
 * @description Renders the personnel sync administration - mapping definitions tab for viewing all the personnel sync mapping
 *  definitions
 */

import React from 'react';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import DeleteIcon from '@mui/icons-material/Delete';
import Button from '@mui/material/Button';

import useClientSidePagination from '@seccon/shared/ui/hooks/useClientSidePagination';
import SortableCell from '@seccon/shared/ui/components/SortableCell';
import NoResults from '@seccon/shared/ui/components/NoResults';
import { rowStyles, TableActionsCell } from '@seccon/shared/ui/components/TableActionsCell';
import { ASC } from '@seccon/shared/utils/sort-compare';

import HeaderActions from './HeaderActions';
import useMappingDefinitionList from '../hooks/useMappingDefinitionList';
import useDeleteMappingDefinitions from '../hooks/useDeleteMappingDefinitions';

const styles = {
  container: {
    alignItems: 'center',
    display: 'flex',
    width: '100%'
  },
  loadingContainer: {
    alignItems: 'center'
  },
  noResults: {
    marginTop: 3
  },
  table: {
    width: '100%',

    '& td': {
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word'
    }
  },
  tableContainer: {
    minWidth: '925px',
    width: '50%'
  },
  tableHeader: {
    label: {
      display: 'flex',
      justifyContent: 'center'
    }
  },
  tableRow: {
    ...rowStyles
  },
  tablePaginationContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%'
  }
};

const MappingDefinitions = () => {
  // Retrieve the mapping definition list and a callback function to refresh the data
  const { data: mappingDefinitionList, error, refresh } = useMappingDefinitionList();

  // Paginate the data and retrieve callbacks for handling various table events
  const {
    handleChangeRowsPerPage,
    handlePageChange,
    handleSort,
    page,
    pageData,
    order,
    orderBy,
    rowsPerPage,
    total
  } = useClientSidePagination({
    data: mappingDefinitionList,
    defaultOrder: ASC,
    defaultOrderBy: 'name'
  });

  // Retrieve a callback function to handle the delete action (and pass the refresh callback function)
  const handleDelete = useDeleteMappingDefinitions(refresh);

  // If the data has not loaded yet, display a loading overlay
  if (mappingDefinitionList === null && !error) {
    return (
      <Stack spacing={3} sx={styles.loadingContainer}>
        <Skeleton animation='wave' variant='rectangular' height={40} width='50%' />
        <Skeleton animation='wave' variant='rectangular' height={40} width='50%' />
        <Skeleton animation='wave' variant='rectangular' height={40} width='50%' />
        <Skeleton animation='wave' variant='rectangular' height={40} width='50%' />
        <Skeleton animation='wave' variant='rectangular' height={40} width='50%' />
        <Skeleton animation='wave' variant='rectangular' height={40} width='50%' />
      </Stack>
    );
  }

  // If an error occured, display an error banner
  if (error) {
    return (
      <NoResults sx={styles.noResults}>
        Personnel mapping definitions could not be loaded.
      </NoResults>
    );
  }

  // If the mapping definition list is empty, display a no results overlay
  if (mappingDefinitionList.length === 0) {
    return (
      <NoResults sx={styles.noResults}>
        No mapping definitions to display.
      </NoResults>
    );
  }

  return (
    <>
      <HeaderActions onDelete={() => handleDelete()} />
      <Stack sx={styles.container} spacing={3}>
        <Paper sx={styles.tableContainer}>
          <TableContainer>
            <Table sx={styles.table}>
              <TableHead>
                <TableRow>
                  <SortableCell
                    id='name'
                    onSort={handleSort}
                    order={order}
                    orderBy={orderBy}
                  >
                    Name
                  </SortableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pageData.map((row) => (
                  <TableRow key={`row${ row.id }`} sx={styles.tableRow}>
                    <TableCell>
                      {row.name}
                    </TableCell>
                    <TableActionsCell>
                      <Button
                        onClick={() => handleDelete(row.id, row.name)}
                        startIcon={<DeleteIcon />}
                        variant='text'
                      >
                        Delete
                      </Button>
                    </TableActionsCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            component='div'
            count={total}
            rowsPerPage={rowsPerPage}
            page={Math.min(page, Math.ceil(total / rowsPerPage) - 1)}
            onPageChange={(_, value) => handlePageChange(value)}
            onRowsPerPageChange={(evt) => handleChangeRowsPerPage(parseInt(evt.target.value, 10))}
          />
        </Paper>
      </Stack>
    </>
  );
};

export default MappingDefinitions;
